import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  IconButton,
  InputBase,
  TextField,
  Typography,
} from '@mui/material';
import type { ResourceSearchItem, RoleResponse, WorkItemResponse } from '@timelog/pm-shared';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useAllocateWithBooking,
  useRpAvailability,
  useRpProjects,
  useRpResources,
  useRpResourceSearch,
  useRpRoles,
} from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MicroLabel, MonoText } from '@/components/ui';
import { formatHours, formatShortDate, useDebouncedValue, addDaysIso, todayIso } from '@/lib';
import { isoToEpochDay } from '@/lib/epochDay';
import { stableColor } from '@/lib/roleColor';

export type FindAllocateDrawerProps = {
  open: boolean;
  onClose: () => void;
  onAllocated: (message: string) => void;
};

/** What the "competency or person" combobox resolved to. */
type CapabilitySelection =
  { kind: 'role'; role: RoleResponse } | { kind: 'person'; person: ResourceSearchItem };

type CapabilityOption =
  | { kind: 'role'; id: string; label: string; role: RoleResponse }
  | { kind: 'person'; id: string; label: string; sub: string; person: ResourceSearchItem };

/** Whether the target is generic demand or the highlighted person. */
type Pick = { kind: 'generic' } | { kind: 'person'; resourceId: string };

const connectionTier = (item: ResourceSearchItem, roleId: string | null): number => {
  const entry = item.roles.find((role) => role.roleId === roleId);
  return entry ? (entry.isPrimary ? 2 : 1) : 0;
};

const TIER_KEYS = {
  2: 'tier.Primary',
  1: 'tier.Secondary',
  0: 'tier.Other',
} as const satisfies Record<number, string>;

const tierKey = (tier: number) => TIER_KEYS[tier === 2 ? 2 : tier === 1 ? 1 : 0];

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!.toUpperCase())
    .slice(0, 2)
    .join('');

const FitChip = ({ free, needed }: { free: number; needed: number }) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const fullyBooked = free <= 0;
  const fits = !fullyBooked && free >= needed;
  const palette = fullyBooked
    ? { bg: 'var(--color-error-10)', fg: 'var(--color-error-100)' }
    : fits
      ? { bg: 'var(--color-success-10)', fg: 'var(--color-success-100)' }
      : { bg: 'var(--color-warning-10)', fg: 'var(--fg-warning-text)' };
  return (
    <Box
      data-automation-id="fit-chip"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 20,
        px: 1,
        borderRadius: 'var(--border-radius-pill)',
        bgcolor: palette.bg,
      }}
    >
      <Typography variant="label" sx={{ color: palette.fg }}>
        {fullyBooked
          ? t('findAllocate.FitFullyBooked')
          : fits
            ? t('findAllocate.FitFits')
            : t('findAllocate.FitShort', { hours: formatHours(Math.ceil(needed - free)) })}
      </Typography>
    </Box>
  );
};

/**
 * Find & Allocate (the design's out-of-band staffing request flow): pick a
 * project, a competency or person, a date range and hours; candidates rank by
 * connection tier then free capacity (capacity − booked over the range, from
 * /rp/availability). Committing creates a project-level allocation — named,
 * or a generic slot to resolve later — and spreads the hours over the range.
 */
export const FindAllocateDrawer = ({ open, onClose, onAllocated }: FindAllocateDrawerProps) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const [project, setProject] = useState<WorkItemResponse | null>(null);
  const [selection, setSelection] = useState<CapabilitySelection | null>(null);
  const [capabilityQuery, setCapabilityQuery] = useState('');
  const [from, setFrom] = useState(() => todayIso());
  const [to, setTo] = useState(() => addDaysIso(todayIso(), 60));
  const [hours, setHours] = useState('80');
  const [picked, setPicked] = useState<Pick | null>(null);
  const [error, setError] = useState<string | null>(null);

  const projects = useRpProjects(open);
  const roles = useRpRoles();
  const resources = useRpResources();
  const debouncedQuery = useDebouncedValue(capabilityQuery);
  const search = useRpResourceSearch(debouncedQuery, open);

  const fromDay = isoToEpochDay(from);
  const toDay = isoToEpochDay(to);
  const validRange = fromDay !== null && toDay !== null && toDay >= fromDay;
  const needed = Number(hours.replace(',', '.')) || 0;
  const availability = useRpAvailability(from, to, open && validRange);

  const ready = project !== null && selection !== null && validRange;

  const capabilityOptions: CapabilityOption[] = useMemo(() => {
    const query = debouncedQuery.trim().toLowerCase();
    const roleOptions: CapabilityOption[] = (roles.data?.roles ?? [])
      .filter((role) => query === '' || role.name.toLowerCase().includes(query))
      .map((role) => ({ kind: 'role', id: role.id, label: role.name, role }));
    const personOptions: CapabilityOption[] = (search.data?.items ?? []).map((person) => ({
      kind: 'person',
      id: person.resourceId,
      label: person.name,
      sub: person.position ?? person.roles.find((role) => role.isPrimary)?.roleName ?? '',
      person,
    }));
    return [...roleOptions, ...personOptions];
  }, [roles.data, search.data, debouncedQuery]);

  const availabilityById = useMemo(
    () => new Map((availability.data?.resources ?? []).map((entry) => [entry.resourceId, entry])),
    [availability.data],
  );

  /** Person mode collapses the list; role mode ranks EVERY person + generic card. */
  const candidates = useMemo(() => {
    if (!ready) return [];
    if (selection.kind === 'person') {
      return [{ item: selection.person, tier: 0 }];
    }
    // Role mode ranks the whole tenant, not just name matches — availability
    // is the point, so nobody free may be hidden by the search box's text.
    const pool = (resources.data?.resources ?? [])
      .filter((entry) => entry.resourceType === 'PERSON')
      .map((entry) => ({
        item: {
          resourceId: entry.id,
          name: entry.name,
          departmentId: entry.departmentId,
          roles: entry.roles,
          email: null,
          position: null,
          initials: null,
        } satisfies ResourceSearchItem,
        tier: 0,
      }))
      .map(({ item }) => ({
        item,
        tier: connectionTier(item, selection.role.id),
      }));
    pool.sort((a, b) => {
      if (a.tier !== b.tier) return b.tier - a.tier;
      const freeA = freeOf(a.item.resourceId);
      const freeB = freeOf(b.item.resourceId);
      return freeB - freeA || a.item.name.localeCompare(b.item.name);
    });
    return pool;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- freeOf reads availabilityById below
  }, [ready, selection, resources.data, availabilityById]);

  function freeOf(resourceId: string): number {
    const entry = availabilityById.get(resourceId);
    return entry ? entry.capacityHours - entry.bookedHours : 0;
  }

  const allocate = useAllocateWithBooking();

  const reset = () => {
    setProject(null);
    setSelection(null);
    setCapabilityQuery('');
    setPicked(null);
    setError(null);
    setHours('80');
  };

  const close = () => {
    reset();
    onClose();
  };

  const disabled = !ready || needed <= 0 || picked === null || allocate.isPending;

  const commit = () => {
    if (!project || !selection || picked === null || needed <= 0) return;
    const isGeneric = picked.kind === 'generic';
    if (isGeneric && selection.kind !== 'role') return;
    allocate.mutate(
      {
        workItemId: project.id,
        resourceKind: isGeneric ? 'GENERIC' : 'NAMED',
        ...(isGeneric
          ? { roleId: selection.kind === 'role' ? selection.role.id : undefined }
          : { resourceId: picked.kind === 'person' ? picked.resourceId : undefined }),
        hours: needed,
        startDate: from,
        endDate: to,
      },
      {
        onSuccess: () => {
          const who = isGeneric
            ? t('findAllocate.GenericWho', {
                role: selection.kind === 'role' ? selection.role.name : '',
              })
            : (candidates.find(
                (candidate) =>
                  picked.kind === 'person' && candidate.item.resourceId === picked.resourceId,
              )?.item.name ?? t('findAllocate.FallbackResource'));
          onAllocated(
            t('findAllocate.AllocatedToast', {
              hours: formatHours(needed),
              who,
              project: project.name,
              from: formatShortDate(from),
              to: formatShortDate(to),
            }),
          );
          close();
        },
        onError: (mutationError) => setError(mutationError.message),
      },
    );
  };

  const summary =
    picked !== null && project
      ? t('findAllocate.SummaryAllocate', { hours: formatHours(needed), project: project.name })
      : ready
        ? t('findAllocate.SummarySelectResource')
        : t('findAllocate.SummaryPickAll');

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={close}
      slotProps={{ paper: { sx: { width: 472 } } }}
      data-automation-id="find-allocate-drawer"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '11px',
          p: '15px 18px',
          borderBottom: 'var(--border-width-thin) solid var(--border-divider)',
          flex: 'none',
        }}
      >
        <MaterialSymbol name="person_search" size={22} color="var(--color-pink-80)" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h4" component="h2">
            {t('findAllocate.Title')}
          </Typography>
          <Typography variant="label" component="div" sx={{ color: 'var(--fg-3)' }}>
            {t('findAllocate.Subtitle')}
          </Typography>
        </Box>
        <IconButton
          size="tiny"
          onClick={close}
          title={t('findAllocate.Close')}
          data-automation-id="find-allocate-close"
        >
          <MaterialSymbol name="close" size={18} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', p: '16px 18px 14px' }}>
        <Box sx={{ mb: 1.25 }}>
          <MicroLabel color="var(--fg-3)">{t('findAllocate.SectionRequest')}</MicroLabel>
        </Box>

        <Typography
          variant="label"
          component="label"
          sx={{ display: 'block', color: 'var(--fg-2)', mb: 0.5 }}
        >
          {t('pmCommon:term.Project')}
        </Typography>
        <Autocomplete<WorkItemResponse>
          size="small"
          options={projects.data?.projects ?? []}
          value={project}
          onChange={(_event, next) => setProject(next)}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderOption={({ key, ...optionProps }, option) => (
            <li key={key} {...optionProps} data-automation-id="find-project-option">
              {option.name}
              {option.number ? ` · ${option.number}` : ''}
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('findAllocate.ProjectPlaceholder')}
              inputProps={{ ...params.inputProps, 'data-automation-id': 'find-project-input' }}
            />
          )}
          sx={{ mb: 1.5 }}
        />

        <Typography
          variant="label"
          component="label"
          sx={{ display: 'block', color: 'var(--fg-2)', mb: 0.5 }}
        >
          {t('findAllocate.LabelCapability')}
        </Typography>
        <Autocomplete<CapabilityOption>
          size="small"
          options={capabilityOptions}
          filterOptions={(options) => options}
          groupBy={(option) =>
            option.kind === 'role'
              ? t('findAllocate.GroupCompetencies')
              : t('findAllocate.GroupPeople')
          }
          value={
            selection === null
              ? null
              : selection.kind === 'role'
                ? {
                    kind: 'role',
                    id: selection.role.id,
                    label: selection.role.name,
                    role: selection.role,
                  }
                : {
                    kind: 'person',
                    id: selection.person.resourceId,
                    label: selection.person.name,
                    sub: '',
                    person: selection.person,
                  }
          }
          onChange={(_event, next) => {
            setPicked(null);
            setSelection(
              next === null
                ? null
                : next.kind === 'role'
                  ? { kind: 'role', role: next.role }
                  : { kind: 'person', person: next.person },
            );
            if (next?.kind === 'person')
              setPicked({ kind: 'person', resourceId: next.person.resourceId });
          }}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') setCapabilityQuery(value);
          }}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) =>
            option.kind === value.kind && option.id === value.id
          }
          renderOption={({ key, ...optionProps }, option) => (
            <li key={key} {...optionProps} data-automation-id="find-capability-option">
              <MaterialSymbol
                name={option.kind === 'role' ? 'badge' : 'person'}
                size={16}
                color={option.kind === 'role' ? 'var(--color-blue-80)' : 'var(--color-pink-80)'}
              />
              <Box component="span" sx={{ ml: 1 }}>
                {option.label}
                {option.kind === 'person' && option.sub ? ` · ${option.sub}` : ''}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('findAllocate.CapabilityPlaceholder')}
              inputProps={{ ...params.inputProps, 'data-automation-id': 'find-capability-input' }}
            />
          )}
          sx={{ mb: 1.5 }}
        />

        <Box sx={{ display: 'flex', columnGap: '10px', mb: 0.75 }}>
          {(
            [
              {
                label: t('findAllocate.LabelFrom'),
                value: from,
                set: setFrom,
                id: 'find-from-input',
              },
              { label: t('findAllocate.LabelTo'), value: to, set: setTo, id: 'find-to-input' },
            ] as const
          ).map((field) => (
            <Box key={field.id} sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="label"
                component="label"
                sx={{ display: 'block', color: 'var(--fg-2)', mb: 0.5 }}
              >
                {field.label}
              </Typography>
              <InputBase
                type="date"
                value={field.value}
                onChange={(event) => field.set(event.target.value)}
                inputProps={{ 'data-automation-id': field.id }}
                sx={{
                  width: '100%',
                  height: 34,
                  px: 1,
                  border: 'var(--border-width-thin) solid var(--border-subtle)',
                  borderRadius: 'var(--border-radius-small)',
                  fontSize: 14,
                }}
              />
            </Box>
          ))}
          <Box sx={{ width: 96, flex: 'none' }}>
            <Typography
              variant="label"
              component="label"
              sx={{ display: 'block', color: 'var(--fg-2)', mb: 0.5 }}
            >
              {t('pmCommon:term.Hours')}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 34,
                px: 1,
                border: 'var(--border-width-thin) solid var(--border-subtle)',
                borderRadius: 'var(--border-radius-small)',
              }}
            >
              <InputBase
                value={hours}
                onChange={(event) => setHours(event.target.value)}
                inputProps={{
                  'data-automation-id': 'find-hours-input',
                  inputMode: 'decimal',
                  style: { textAlign: 'right' },
                }}
                sx={{ flex: 1, fontFamily: 'var(--font-family-mono)', fontSize: 11 }}
              />
              <Typography variant="label" sx={{ color: 'var(--fg-3)', ml: 0.5 }}>
                {t('pmCommon:unit.HoursShort')}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            m: '20px 0 8px',
          }}
        >
          <MicroLabel color="var(--fg-3)">{t('findAllocate.AvailableResources')}</MicroLabel>
          <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
            {t('findAllocate.RankedByFreeHours')}
          </Typography>
        </Box>

        {!ready && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              rowGap: '9px',
              p: '28px 20px',
            }}
          >
            <MaterialSymbol name="person_search" size={30} color="var(--color-fog-60)" />
            <Typography variant="label" sx={{ color: 'var(--fg-3)', maxWidth: 248 }}>
              {t('findAllocate.EmptyPrompt')}
            </Typography>
          </Box>
        )}

        {ready && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '6px', mb: 1.5 }}>
              <MaterialSymbol name="event" size={15} color="var(--fg-3)" />
              <Typography variant="label" sx={{ color: 'var(--fg-3)' }}>
                {formatShortDate(from)} – {formatShortDate(to)}
              </Typography>
            </Box>

            {selection.kind === 'role' && (
              <Box
                data-automation-id="find-generic-card"
                onClick={() => setPicked({ kind: 'generic' })}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '11px',
                  p: '10px 12px',
                  border: 'var(--border-width-thin) dashed var(--color-coral-80)',
                  borderRadius: 'var(--border-radius-medium)',
                  bgcolor:
                    picked?.kind === 'generic' ? 'var(--color-pink-10)' : 'var(--color-coral-10)',
                  cursor: 'pointer',
                  mb: 1.25,
                }}
              >
                <MaterialSymbol name="groups" size={22} color="var(--color-coral-80)" />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="bodyRegular" component="div">
                    {t('findAllocate.GenericCardTitle', { role: selection.role.name })}
                  </Typography>
                  <Typography variant="label" component="div" sx={{ color: 'var(--fg-3)' }}>
                    {t('findAllocate.GenericCardSub')}
                  </Typography>
                </Box>
                {picked?.kind === 'generic' && (
                  <MaterialSymbol
                    name="check_circle"
                    size={20}
                    color="var(--color-pink-80)"
                    filled
                  />
                )}
              </Box>
            )}

            {candidates.map(({ item, tier }, index) => {
              const entry = availabilityById.get(item.resourceId);
              const capacity = entry?.capacityHours ?? 0;
              const booked = entry?.bookedHours ?? 0;
              const free = capacity - booked;
              const enough = needed > 0 ? free >= needed : free > 0;
              const bookedPct = capacity > 0 ? Math.min(100, (booked / capacity) * 100) : 0;
              const requestPct =
                capacity > 0 ? Math.min(100 - bookedPct, (needed / capacity) * 100) : 0;
              const selected = picked?.kind === 'person' && picked.resourceId === item.resourceId;
              const header =
                selection.kind === 'role' &&
                (index === 0 || candidates[index - 1]!.tier !== tier) ? (
                  <Box sx={{ p: '9px 2px 5px' }}>
                    <MicroLabel color="var(--fg-3)">{t(tierKey(tier))}</MicroLabel>
                  </Box>
                ) : null;
              return (
                <Box key={item.resourceId}>
                  {header}
                  <Box
                    data-automation-id="find-candidate"
                    onClick={() => setPicked({ kind: 'person', resourceId: item.resourceId })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      columnGap: '11px',
                      p: '9px 11px',
                      border: `var(--border-width-thin) solid ${
                        selected ? 'var(--color-pink-80)' : 'var(--border-subtle)'
                      }`,
                      borderRadius: 'var(--border-radius-medium)',
                      bgcolor: selected ? 'var(--color-pink-10)' : 'var(--bg-panel)',
                      cursor: 'pointer',
                      mb: 0.75,
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 30,
                        height: 30,
                        flex: 'none',
                        borderRadius: 'var(--border-radius-pill)',
                        bgcolor: `color-mix(in srgb, ${stableColor(item.resourceId)} 24%, var(--bg-panel))`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="label">
                        {item.initials ?? initialsOf(item.name)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                        <Typography
                          variant="bodyRegular"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item.name}
                        </Typography>
                        {tier === 2 && (
                          <Box
                            component="span"
                            title={t('tier.Primary')}
                            sx={{ display: 'inline-flex' }}
                          >
                            <MaterialSymbol
                              name="star"
                              size={13}
                              color="var(--color-pink-80)"
                              filled
                            />
                          </Box>
                        )}
                      </Box>
                      <Typography
                        variant="label"
                        component="div"
                        sx={{ color: 'var(--fg-3)', mt: '1px' }}
                      >
                        {item.position ?? item.roles.find((role) => role.isPrimary)?.roleName ?? ''}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.75,
                          height: 5,
                          borderRadius: 'var(--border-radius-small)',
                          bgcolor: 'var(--color-fog-20)',
                          overflow: 'hidden',
                          position: 'relative',
                          width: 158,
                          maxWidth: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${String(bookedPct)}%`,
                            bgcolor: 'var(--color-fog-60)',
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            left: `${String(bookedPct)}%`,
                            top: 0,
                            height: '100%',
                            width: `${String(requestPct)}%`,
                            bgcolor: enough ? 'var(--color-blue-80)' : 'var(--color-coral-80)',
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        textAlign: 'right',
                        flex: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        rowGap: '4px',
                      }}
                    >
                      <MonoText
                        size="label"
                        color={
                          free <= 0
                            ? 'var(--color-coral-100)'
                            : enough
                              ? 'var(--color-success-100)'
                              : 'var(--fg-1)'
                        }
                      >
                        {t('findAllocate.FreeHours', { hours: formatHours(Math.round(free)) })}
                      </MonoText>
                      <FitChip free={free} needed={needed} />
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {candidates.length === 0 && (
              <Typography variant="label" component="div" sx={{ color: 'var(--fg-3)', p: 1 }}>
                {t('findAllocate.NoPeople')}
              </Typography>
            )}
          </>
        )}

        {error && (
          <Typography
            variant="label"
            component="div"
            data-automation-id="find-allocate-error"
            sx={{ color: 'var(--color-coral-100)', mt: 1 }}
          >
            {error}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          borderTop: 'var(--border-width-thin) solid var(--border-divider)',
          p: '11px 16px',
          display: 'flex',
          alignItems: 'center',
          columnGap: '10px',
          flex: 'none',
        }}
      >
        <Typography
          variant="label"
          sx={{
            flex: 1,
            minWidth: 0,
            color: 'var(--fg-3)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          data-automation-id="find-allocate-summary"
        >
          {summary}
        </Typography>
        <Button variant="text" color="secondary" size="small" onClick={close}>
          {t('findAllocate.Cancel')}
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={disabled}
          onClick={commit}
          startIcon={<MaterialSymbol name="add" size={16} />}
          data-automation-id="find-allocate-commit"
        >
          {t('findAllocate.Allocate')}
        </Button>
      </Box>
    </Drawer>
  );
};
